import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Button,
  Spinner,
} from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  exchangeSsoLoginCodeV1AuthSsoExchangePost,
} from '../../generated/authentication/authentication';
import {
  clearSsoCodeVerifier,
  getSsoCodeVerifier,
} from '../../lib/sso-code-exchange';
import {
  clearPendingMcpOAuth,
  completeMcpOAuthFlow,
  getPendingMcpOAuth,
} from '../../lib/mcp-oauth';
import {
  requireLocalStorageSet,
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet,
} from '../../lib/safe-storage';

const CALLBACK_TIMEOUT_MS = 15_000;

function storeAuthTokens(accessToken: string, refreshToken: string): void {
  const previousAccessToken = safeLocalStorageGet('auth_token');
  const previousRefreshToken = safeLocalStorageGet('refresh_token');

  try {
    requireLocalStorageSet('auth_token', accessToken);
    requireLocalStorageSet('refresh_token', refreshToken);
  } catch (error) {
    if (previousAccessToken === null) {
      safeLocalStorageRemove('auth_token');
    } else {
      safeLocalStorageSet('auth_token', previousAccessToken);
    }
    if (previousRefreshToken === null) {
      safeLocalStorageRemove('refresh_token');
    } else {
      safeLocalStorageSet('refresh_token', previousRefreshToken);
    }
    throw error;
  }
}

export default function SSOCallbackPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Stable ref so the effect doesn't re-run when AuthProvider re-renders
  // (refreshUser is not memoized, so its identity changes every render).
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;

  useEffect(() => {
    let active = true;
    let settled = false;
    const abortController = new AbortController();
    let callbackTimeout: ReturnType<typeof setTimeout> | undefined;

    const isActive = () => active && !settled;

    const stopWithError = (message: string) => {
      if (!isActive()) return;
      settled = true;
      if (callbackTimeout) clearTimeout(callbackTimeout);
      setError(message);
    };

    const finalizeLogin = async () => {
      const searchParams = new URLSearchParams(window.location.search);

      const errorMessage = searchParams.get('message');
      if (errorMessage) {
        stopWithError(errorMessage);
        return;
      }

      let storedToken: string;
      const hasExchangeCode = searchParams.has('code');
      const exchangeCode = searchParams.get('code');
      if (hasExchangeCode) {
        // Scrub any code and fragment before validating either value so error
        // states cannot leave credentials in browser history or referrers.
        window.history.replaceState({}, '', '/auth/sso/callback');
        if (!exchangeCode) {
          stopWithError('SSO login could not be verified. Please restart sign-in.');
          return;
        }
        const codeVerifier = getSsoCodeVerifier();
        if (!codeVerifier) {
          stopWithError('SSO login could not be verified. Please restart sign-in.');
          return;
        }

        try {
          const response = await exchangeSsoLoginCodeV1AuthSsoExchangePost({
            code: exchangeCode,
            code_verifier: codeVerifier,
          }, abortController.signal);
          if (!isActive()) return;
          storeAuthTokens(response.access_token, response.refresh_token);
          if (callbackTimeout) clearTimeout(callbackTimeout);
          storedToken = response.access_token;
          clearSsoCodeVerifier();
        } catch (exchangeError: any) {
          if (!isActive()) return;
          clearSsoCodeVerifier();
          const detail = exchangeError?.response?.data?.detail;
          const message = exchangeError?.response?.data?.message;
          stopWithError(
            (typeof detail === 'string' && detail)
              || (typeof message === 'string' && message)
              || 'SSO login could not be completed. Please restart sign-in.',
          );
          return;
        }
      } else {
        // Temporary compatibility for an old backend during a split rollout.
        // Do not use this branch after an exchange code exists: an invalid code
        // or proof must fail closed instead of silently accepting a fragment.
        const rawFragment = window.location.hash;
        const fragment = new URLSearchParams(rawFragment.slice(1));
        if (rawFragment) {
          window.history.replaceState({}, '', '/auth/sso/callback');
        }
        const accessToken = fragment.get('access_token');
        const refreshToken = fragment.get('refresh_token');
        const tokenType = fragment.get('token_type');
        if (!accessToken || !refreshToken || tokenType !== 'bearer') {
          stopWithError('SSO login could not be verified. Please restart sign-in.');
          return;
        }

        try {
          storeAuthTokens(accessToken, refreshToken);
          if (callbackTimeout) clearTimeout(callbackTimeout);
          storedToken = accessToken;
          clearSsoCodeVerifier();
        } catch (storageError: any) {
          stopWithError(storageError?.message || 'Unable to store SSO login details.');
          return;
        }
      }

      try {
        await refreshUserRef.current();
      } catch (e: any) {
        if (isActive()) {
          console.warn('SSO callback: user profile refresh failed', e?.message || e);
        }
      }
      if (!isActive()) return;

      const pendingMcpOAuth = getPendingMcpOAuth();
      if (pendingMcpOAuth) {
        try {
          const redirectUrl = await completeMcpOAuthFlow(pendingMcpOAuth, storedToken);
          if (!isActive()) return;
          settled = true;
          if (callbackTimeout) clearTimeout(callbackTimeout);
          clearPendingMcpOAuth();
          window.location.href = redirectUrl;
          return;
        } catch (e: any) {
          if (!isActive()) return;
          clearPendingMcpOAuth();
          stopWithError(e?.message || 'Failed to complete MCP authorization.');
          return;
        }
      }

      if (!isActive()) return;
      settled = true;
      if (callbackTimeout) clearTimeout(callbackTimeout);
      navigate('/home', { replace: true });
    };

    // Deferring the start lets React StrictMode finish its development-only
    // setup/cleanup replay before this single-use code can be consumed.
    const startTimer = setTimeout(() => {
      if (isActive()) void finalizeLogin();
    }, 0);

    callbackTimeout = setTimeout(() => {
      if (!isActive()) return;
      settled = true;
      abortController.abort();
      clearSsoCodeVerifier();
      setError(
        'SSO login is taking too long. Your tokens may be invalid or the server is unreachable.',
      );
    }, CALLBACK_TIMEOUT_MS);

    return () => {
      active = false;
      clearTimeout(startTimer);
      if (callbackTimeout) clearTimeout(callbackTimeout);
      abortController.abort();
    };
  // navigate is stable (React Router guarantees this). refreshUser is
  // captured via ref to avoid re-triggering the effect on every AuthProvider render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <h1 className="font-title text-h2 text-charcoal dark:text-gray-100">
                SSO Login Failed
              </h1>
              <p className="text-gray-500 dark:text-gray-400">{error}</p>
              <div className="space-y-2">
                <Link to="/login">
                  <Button className="w-full">Back to Login</Button>
                </Link>
                <button
                  type="button"
                  className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
                  onClick={() => {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('refresh_token');
                    window.location.href = '/login';
                  }}
                >
                  Clear session &amp; return to login
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center p-4">
      <div className="text-center">
        <Spinner size="xl" variant="primary" />
        <p className="mt-4 text-gray-500 dark:text-gray-400">
          Completing SSO login...
        </p>
        <button
          type="button"
          className="mt-6 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
          onClick={() => {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
          }}
        >
          Cancel &amp; return to login
        </button>
      </div>
    </div>
  );
}
